import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, CheckCircle2, Minus, Plus, X } from "lucide-react";
import { ATT_DEVICES, ATT_PLANS, ATT_BLUE, ATT_PORTING_INFO, ATT_CARRIERS, ATT_TRADE_PHONES, ATT_TRADE_STORAGE, ATT_TRADE_CONDITIONS } from "@/attData";

const API = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  "w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 font-body text-sm text-white placeholder:text-white/30 focus:border-[#00A8E0] focus:outline-none transition-colors duration-300";

const DeviceRow = ({ name, qty, onChange, testid }) => (
  <div className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 transition-colors duration-300 ${qty > 0 ? "border-[#00A8E0]/60 bg-[#00A8E0]/5" : "border-white/10 bg-black/30"}`} data-testid={testid}>
    <span className="font-body text-sm text-white/80">{name}</span>
    <div className="flex items-center gap-2">
      <button type="button" aria-label={`Remove one ${name}`} onClick={() => onChange(Math.max(0, qty - 1))} className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 text-white/60 hover:border-[#00A8E0] hover:text-white transition-colors">
        <Minus size={13} />
      </button>
      <span className={`w-6 text-center font-body text-sm ${qty > 0 ? "text-[#00A8E0] font-bold" : "text-white/40"}`}>{qty}</span>
      <button type="button" aria-label={`Add one ${name}`} onClick={() => onChange(qty + 1)} className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 text-white/60 hover:border-[#00A8E0] hover:text-white transition-colors">
        <Plus size={13} />
      </button>
    </div>
  </div>
);

const DeviceGroup = ({ label, items, values, setValues, prefix }) => (
  <div>
    <p className="font-body text-xs tracking-[0.25em] uppercase text-white/50 mb-3">{label}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {items.map((name) => (
        <DeviceRow
          key={name}
          name={name}
          qty={values[name] || 0}
          onChange={(q) => setValues((v) => ({ ...v, [name]: q }))}
          testid={`${prefix}-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        />
      ))}
    </div>
  </div>
);

const YesNo = ({ label, value, onChange, testid, info, options = ["Yes", "No"] }) => (
  <div>
    <p className="font-body text-xs tracking-[0.25em] uppercase text-white/50 mb-3">{label}</p>
    <div className="flex flex-wrap gap-3">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          data-testid={`${testid}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => onChange(opt)}
          className={`rounded-full px-6 py-2.5 font-body text-sm font-semibold border transition-colors duration-300 ${value === opt ? "border-[#00A8E0] bg-[#00A8E0]/15 text-[#00A8E0]" : "border-white/15 text-white/60 hover:border-white/40"}`}
        >
          {opt}
        </button>
      ))}
    </div>
    {info && <p className="mt-3 font-body text-xs leading-relaxed text-white/40">{info}</p>}
  </div>
);

export const PreorderForm = ({ audience }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", wireless_plan: "", internet_plan: "", lines: "1", notes: "", porting: "", trade_in: "", current_carrier: "", account_number: "", transfer_pin: "", trade_in_phone: "", trade_in_storage: "", trade_in_condition: "", trade_in_carrier: "", trade_in_imei: "", hero_discount: "" });
  const [phones, setPhones] = useState({});
  const [tablets, setTablets] = useState({});
  const [watches, setWatches] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [cart, setCart] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("att_cart");
      if (!raw) return;
      const c = JSON.parse(raw);
      setCart(c);
      const setters = { Phone: setPhones, Tablet: setTablets, Watch: setWatches };
      if (c.device && setters[c.device.category]) setters[c.device.category]((v) => ({ ...v, [c.device.name]: 1 }));
      (c.addons || []).forEach((a) => setters[a.category] && setters[a.category]((v) => ({ ...v, [a.name]: 1 })));
      setForm((f) => ({
        ...f,
        trade_in: c.tradeIn || f.trade_in,
        wireless_plan: audience === "residential" && c.plan ? c.plan : f.wireless_plan,
      }));
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearCart = () => {
    sessionStorage.removeItem("att_cart");
    setCart(null);
    setPhones({});
    setTablets({});
    setWatches({});
    setForm((f) => ({ ...f, wireless_plan: "", trade_in: "" }));
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const pick = (obj, category) => Object.entries(obj).filter(([, q]) => q > 0).map(([name, qty]) => ({ category, name, qty }));

  const maxLines = audience === "business" ? 20 : 10;
  const lineOptions = Array.from({ length: maxLines }, (_, i) => String(i + 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone number.");
      return;
    }
    setSending(true);
    try {
      const devices = [...pick(phones, "Phone"), ...pick(tablets, "Tablet"), ...pick(watches, "Watch")];
      const res = await fetch(`${API}/api/att/preorder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          audience,
          lines: form.lines,
          devices,
          configuration: cart?.summary || null,
          est_monthly: cart?.estMonthly || null,
          est_onetime: cart?.estOnetime || null,
        }),
      });
      if (!res.ok) throw new Error();
      sessionStorage.removeItem("att_cart");
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again or call us at (470) 814-0464.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="att-preorder-success" className="rounded-3xl border border-[#00A8E0]/40 bg-black/30 p-10 md:p-14 text-center">
        <CheckCircle2 size={48} className="mx-auto" style={{ color: ATT_BLUE }} strokeWidth={1.2} />
        <h3 className="mt-6 font-serif-display text-3xl md:text-4xl text-white">Pre-order received.</h3>
        <p className="mt-4 font-body font-light text-base md:text-lg text-white/60 max-w-xl mx-auto">
          Thank you! Our team will confirm current promos and pricing for your selections and reach out to place your
          order.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-[#0a192f] p-8 md:p-10 space-y-7" data-testid={`att-preorder-form-${audience}`}>
      <div>
        <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase" style={{ color: ATT_BLUE }}>Pre-Order Form</p>
        <h2 className="mt-3 font-serif-display text-3xl md:text-4xl text-white">
          {audience === "business" ? "Business pre-order." : "Residential pre-order."}
        </h2>
        <p className="mt-3 font-body font-light text-sm md:text-base text-white/55">
          Pick your devices and plan below — we will contact you about your pre-order and apply every promo you qualify for.
        </p>
      </div>

      {cart && (
        <div className="rounded-2xl border border-[#00A8E0]/50 bg-[#00A8E0]/10 p-5" data-testid="att-cart-summary">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="h-16 w-16 shrink-0 flex items-center justify-center bg-white rounded-xl">
                <img src={cart.device.img} alt={cart.device.name} className="max-h-14 w-auto object-contain" />
              </div>
              <div>
                <p className="font-body text-xs tracking-[0.25em] uppercase" style={{ color: ATT_BLUE }}>Your configured build</p>
                <div className="mt-2 space-y-0.5">
                  {cart.summary.split("\n").map((line, i) => (
                    <p key={i} className={`font-body ${i === 0 ? "text-sm font-bold text-white" : "text-xs text-white/60"}`}>{line}</p>
                  ))}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={clearCart}
              data-testid="att-cart-clear"
              aria-label="Remove configured build"
              className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-white/50 hover:border-red-400 hover:text-red-400 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input data-testid="att-name-input" className={inputCls} placeholder={audience === "business" ? "Contact name *" : "Full name *"} value={form.name} onChange={set("name")} />
        {audience === "business" ? (
          <input data-testid="att-business-name-input" className={inputCls} placeholder="Business name" value={form.business_name} onChange={set("business_name")} />
        ) : (
          <input data-testid="att-phone-input" className={inputCls} placeholder="Phone number *" value={form.phone} onChange={set("phone")} />
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input data-testid="att-email-input" type="email" className={inputCls} placeholder="Email address *" value={form.email} onChange={set("email")} />
        {audience === "business" ? (
          <input data-testid="att-phone-input-biz" className={inputCls} placeholder="Phone number *" value={form.phone} onChange={set("phone")} />
        ) : (
          <select data-testid="att-lines-select" className={`${inputCls} text-white`} value={form.lines} onChange={set("lines")}>
            {lineOptions.map((n) => (
              <option key={n} value={n} className="bg-[#0a192f]">{n} line{n === "1" ? "" : "s"}</option>
            ))}
          </select>
        )}
      </div>
      {audience === "business" && (
        <div>
          <p className="font-body text-xs tracking-[0.25em] uppercase text-white/50 mb-3">How many lines? (up to 20)</p>
          <select data-testid="att-lines-select" className={`${inputCls} text-white`} value={form.lines} onChange={set("lines")}>
            {lineOptions.map((n) => (
              <option key={n} value={n} className="bg-[#0a192f]">{n} line{n === "1" ? "" : "s"}</option>
            ))}
          </select>
        </div>
      )}

      <YesNo
        label="Are you a teacher, police officer, doctor, nurse, or military? — 20% off each line"
        value={form.hero_discount}
        onChange={setVal("hero_discount")}
        testid="att-hero-discount"
        options={["Teacher", "Police Officer", "Doctor", "Nurse", "Military", "No"]}
        info="Qualifying heroes get 20% off each line. Verification will be required (work ID, badge, or proof of service) before the discount is applied."
      />

      <YesNo
        label="Are you porting (keeping) your current number?"
        value={form.porting}
        onChange={setVal("porting")}
        testid="att-porting"
        info={ATT_PORTING_INFO}
      />

      {form.porting === "Yes" && (
        <div className="rounded-2xl border border-[#00A8E0]/30 bg-[#00A8E0]/5 p-5 space-y-4" data-testid="att-porting-details">
          <p className="font-body text-xs tracking-[0.25em] uppercase" style={{ color: ATT_BLUE }}>Number transfer details</p>
          <div>
            <p className="font-body text-xs tracking-[0.25em] uppercase text-white/50 mb-2">What carrier are you coming from?</p>
            <select data-testid="att-carrier-select" className={`${inputCls} ${form.current_carrier ? "text-white" : "text-white/30"}`} value={form.current_carrier} onChange={set("current_carrier")}>
              <option value="" disabled>Choose your current carrier</option>
              {ATT_CARRIERS.map((c) => (
                <option key={c} value={c} className="bg-[#0a192f] text-white">{c}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="att-account-number-input" className={inputCls} placeholder="Current carrier account number" value={form.account_number} onChange={set("account_number")} />
            <input data-testid="att-transfer-pin-input" className={inputCls} placeholder="Transfer PIN (from your carrier)" value={form.transfer_pin} onChange={set("transfer_pin")} />
          </div>
          <p className="font-body text-[11px] leading-relaxed text-white/40">
            Don't have these handy? No problem — leave them blank and we'll help you get them when we call. Your
            transfer PIN comes from your current carrier's app or by dialing their support line.
          </p>
        </div>
      )}

      <YesNo
        label="Are you trading in a phone?"
        value={form.trade_in}
        onChange={setVal("trade_in")}
        testid="att-tradein"
      />

      {form.trade_in === "Yes" && (
        <div className="rounded-2xl border border-[#00A8E0]/30 bg-[#00A8E0]/5 p-5 space-y-4" data-testid="att-tradein-details">
          <p className="font-body text-xs tracking-[0.25em] uppercase" style={{ color: ATT_BLUE }}>Trade-in details</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="font-body text-xs tracking-[0.25em] uppercase text-white/50 mb-2">What phone are you trading in?</p>
              <select data-testid="att-tradein-phone-select" className={`${inputCls} ${form.trade_in_phone ? "text-white" : "text-white/30"}`} value={form.trade_in_phone} onChange={set("trade_in_phone")}>
                <option value="" disabled>Choose your phone</option>
                {Object.entries(ATT_TRADE_PHONES).map(([brand, models]) => (
                  <optgroup key={brand} label={brand}>
                    {models.map((m) => (
                      <option key={m} value={m} className="bg-[#0a192f] text-white">{m}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <p className="font-body text-xs tracking-[0.25em] uppercase text-white/50 mb-2">Storage size</p>
              <select data-testid="att-tradein-storage-select" className={`${inputCls} ${form.trade_in_storage ? "text-white" : "text-white/30"}`} value={form.trade_in_storage} onChange={set("trade_in_storage")}>
                <option value="" disabled>Choose storage</option>
                {ATT_TRADE_STORAGE.map((s) => (
                  <option key={s} value={s} className="bg-[#0a192f] text-white">{s}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="font-body text-xs tracking-[0.25em] uppercase text-white/50 mb-2">Condition of the phone</p>
              <select data-testid="att-tradein-condition-select" className={`${inputCls} ${form.trade_in_condition ? "text-white" : "text-white/30"}`} value={form.trade_in_condition} onChange={set("trade_in_condition")}>
                <option value="" disabled>Choose condition</option>
                {ATT_TRADE_CONDITIONS.map((c) => (
                  <option key={c} value={c} className="bg-[#0a192f] text-white">{c}</option>
                ))}
              </select>
            </div>
            <div>
              <p className="font-body text-xs tracking-[0.25em] uppercase text-white/50 mb-2">What carrier is that phone with?</p>
              <select data-testid="att-tradein-carrier-select" className={`${inputCls} ${form.trade_in_carrier ? "text-white" : "text-white/30"}`} value={form.trade_in_carrier} onChange={set("trade_in_carrier")}>
                <option value="" disabled>Choose carrier</option>
                {ATT_CARRIERS.map((c) => (
                  <option key={c} value={c} className="bg-[#0a192f] text-white">{c}</option>
                ))}
              </select>
            </div>
          </div>
          <input data-testid="att-tradein-imei-input" className={inputCls} placeholder="IMEI number (dial *#06# on that phone to find it)" value={form.trade_in_imei} onChange={set("trade_in_imei")} />
          <p className="font-body text-[11px] leading-relaxed text-white/40">
            Your IMEI helps us lock in an exact trade-in value. Can't find it now? Leave it blank and we'll grab it
            together when we call.
          </p>
        </div>
      )}

      <DeviceGroup label="Phones" items={ATT_DEVICES.phones} values={phones} setValues={setPhones} prefix={`att-phone`} />
      <DeviceGroup label="Tablets" items={ATT_DEVICES.tablets} values={tablets} setValues={setTablets} prefix={`att-tablet`} />
      <DeviceGroup label="Watches" items={ATT_DEVICES.watches} values={watches} setValues={setWatches} prefix={`att-watch`} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className="font-body text-xs tracking-[0.25em] uppercase text-white/50 mb-3">Wireless plan</p>
          <select data-testid="att-wireless-plan-select" className={`${inputCls} ${form.wireless_plan ? "text-white" : "text-white/30"}`} value={form.wireless_plan} onChange={set("wireless_plan")}>
            <option value="" disabled>Choose a wireless plan (optional)</option>
            {ATT_PLANS[audience].wireless.map((p) => (
              <option key={p} value={p} className="bg-[#0a192f] text-white">{p}</option>
            ))}
          </select>
        </div>
        <div>
          <p className="font-body text-xs tracking-[0.25em] uppercase text-white/50 mb-3">Internet plan</p>
          <select data-testid="att-internet-plan-select" className={`${inputCls} ${form.internet_plan ? "text-white" : "text-white/30"}`} value={form.internet_plan} onChange={set("internet_plan")}>
            <option value="" disabled>Choose an internet plan (optional)</option>
            {ATT_PLANS[audience].internet.map((p) => (
              <option key={p} value={p} className="bg-[#0a192f] text-white">{p}</option>
            ))}
          </select>
        </div>
      </div>

      <textarea data-testid="att-notes-input" rows={3} className={inputCls} placeholder="Anything else? Trade-in devices, current carrier, install address... (optional)" value={form.notes} onChange={set("notes")} />

      <button
        type="submit"
        disabled={sending}
        data-testid="att-preorder-submit"
        className="btn-sweep w-full inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 font-body text-sm font-semibold tracking-wide text-white disabled:opacity-60"
        style={{ backgroundColor: ATT_BLUE }}
      >
        {sending ? "Submitting..." : "Submit My Pre-Order"} <Send size={16} />
      </button>
      <p className="font-body text-[11px] leading-relaxed text-white/35 text-center">
        No payment is taken now. We'll confirm pricing, promos, and availability with you before anything is ordered.
      </p>
    </form>
  );
};

export default function ATTServicePage() {
  const { audience } = useParams();
  if (audience !== "residential" && audience !== "business") return <Navigate to="/att" replace />;
  return <Navigate to={`/att#${audience}`} replace />;
}
